import React, { useState } from 'react'
import {
  displayDollar,
  formatStatus,
  getFileKeyFromSignedUrl,
  getFormattedDate,
  getTableClassNames,
  getToolTipText,
} from '../../lib/utils/helpers'
import {
  EyeIcon,
  ToolTip,
  ToolTipBlack,
  ExternalLinkIcon,
  DownloadIcon,
} from '../../lib/components'
import './order.scss'
import { stuckAtInvestmentMessage, stuckAtWithdrawalMessage } from '../../lib/utils/message'
import { OrderCardProps } from '../../lib/types'
import { documentServices, kycServices, opportunityServices } from '../../services'
import { CircularProgress } from '@mui/material'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

function OrderCard({
  order_id,
  opportunity_title,
  order_creation_date,
  investor_portfolio_id,
  investor_name,
  investor_email,
  currency_code,
  order_amount,
  total_amount_to_be_transferred,
  investment_completed,
  withdrawal_completed,
  completed_steps,
  handleOrderClick,
  unit_allocation_date,
  investor_account_status,
  welcome_letter_link,
  agreement_document_id,
  investor_kyc_id,
  payment_copy,
  transaction_reference_number,
  order_status,
  order_type,
  columnOrder = [
    'order_id',
    'order_type',
    'order_status',
    'opportunity_title',
    'order_creation_date',
    'unit_allocation_date',
    'investor_portfolio_id',
    'investor_name',
    'investor_email',
    'kyc',
    'welcome_letter',
    'client_agreement',
    'order_amount',
    'total_amount_to_be_transferred',
    'payment_copy',
    'order_completed',
    'order_details',
  ],
}: OrderCardProps) {
  const dispatch = useDispatch()
  const [isAgreementLoading, setIsAgreementLoading] = useState(false)
  const [isKYCReportLoading, setIsKYCReportLoading] = useState(false)

  const formattedDate = (date: string) => getFormattedDate(new Date(date), 'D MMM YYYY [at] h:mm A')

  const getDocumentDownloadLink = async (documentId: string) => {
    const response = await documentServices.getDocumentDownloadLink(documentId)

    if (response.status === 200 && response.data) {
      return response.data
    }
  }

  const getKYCReportLink = async (investorKycId: string) => {
    const response = await kycServices.generateKYCReportLink(investorKycId)

    if (response.status === 200 && response.data) {
      return response.data
    }
  }

  const handleWelcomeLetterBtnClick = () => {
    logCrmEvent(EngagementEvents.CLICKED_VIEW_WELCOME_LETTER, {
      page: 'orders',
      orderId: order_id,
      investorEmail: investor_email,
    })
    window.open(welcome_letter_link)
  }

  const handleDownloadAgreementBtnClick = async () => {
    try {
      setIsAgreementLoading(true)
      const downloadLink = await getDocumentDownloadLink(agreement_document_id)
      setIsAgreementLoading(false)

      logCrmEvent(EngagementEvents.CLICKED_DOWNLOAD_CLIENT_AGREEMENT, {
        page: 'orders',
        orderId: order_id,
        investorEmail: investor_email,
      })

      window.open(downloadLink)
    } catch (error) {
      console.error(error)
    }
  }

  const handleGenerateKYCReportBtnClick = async () => {
    try {
      setIsKYCReportLoading(true)
      const { reportLink } = await getKYCReportLink(investor_kyc_id)
      setIsKYCReportLoading(false)

      logCrmEvent(EngagementEvents.CLICKED_VIEW_KYC_REPORT, {
        page: 'orders',
        orderId: order_id,
        investorEmail: investor_email,
      })
      window.open(reportLink)
    } catch (error) {
      console.error(error)
    }
  }

  const handleFilePreview = async (fileKey: string) => {
    fileKey = (fileKey as any).replaceAll('/', '%2F')

    const signedUrl = getFileKeyFromSignedUrl(fileKey)
    const data = await opportunityServices.previewFile(signedUrl)
    if (data && data.data && data.status === 200) return data.data.url
    else {
      dispatch(
        showBanner({
          text: (data.data && data.data.message) || 'Something went wrong preview document',
          variant: 'error-banner',
        }),
      )
      return ''
    }
  }

  const handlePaymentCopyClick = async () => {
    const url = await handleFilePreview(payment_copy)

    logCrmEvent(EngagementEvents.CLICKED_VIEW_SWIFT_COPY, {
      page: 'orders',
      orderId: order_id,
      investorEmail: investor_email,
    })

    url && window.open(url, '_blank')
  }

  const getText = () => {
    return getToolTipText({
      investor_account_status,
      investment_completed,
      completed_steps,
      payment_copy,
      transaction_reference_number,
      order_status,
      order_type,
      withdrawal_completed,
    })
  }

  const getOrderTypeText = (order_type: string) => {
    switch (order_type) {
      case 'BUY':
        return 'Investment'
      case 'SELL':
        return 'Withdrawal'
      default:
        return order_type
    }
  }

  const getCellContent = (columnId: string): JSX.Element => {
    const tooltipText = getText()

    switch (columnId) {
      case 'order_id':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{order_id}</span>
          </ToolTip>
        )
      case 'order_type':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{getOrderTypeText(order_type)}</span>
          </ToolTip>
        )
      case 'order_status':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{formatStatus(order_status)}</span>
          </ToolTip>
        )
      case 'opportunity_title':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{opportunity_title}</span>
          </ToolTip>
        )
      case 'order_creation_date':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>
              {order_creation_date ? formattedDate(order_creation_date) : 'Not available'}
            </span>
          </ToolTip>
        )
      case 'unit_allocation_date':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>
              {order_type === 'BUY' && (
                <>{unit_allocation_date ? formattedDate(unit_allocation_date) : 'Not available'}</>
              )}
              {order_type === 'SELL' && <>N.A.</>}
            </span>
          </ToolTip>
        )
      case 'investor_portfolio_id':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{investor_portfolio_id}</span>
          </ToolTip>
        )
      case 'investor_name':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{investor_name}</span>
          </ToolTip>
        )
      case 'investor_email':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{investor_email}</span>
          </ToolTip>
        )
      case 'kyc':
        return (
          <>
            {!isKYCReportLoading ? (
              <ExternalLinkIcon
                classes={['pointer']}
                onClick={handleGenerateKYCReportBtnClick}
                dataTestId='orders-table-kyc-report-view-icon'
              />
            ) : (
              <CircularProgress size={20} />
            )}
          </>
        )
      case 'welcome_letter':
        return (
          <>
            {welcome_letter_link ? (
              <DownloadIcon
                classes={['pointer']}
                onClick={handleWelcomeLetterBtnClick}
                dataTestId='orders-table-welcome-letter-download-icon'
              />
            ) : (
              'N.A.'
            )}
          </>
        )
      case 'client_agreement':
        return (
          <>
            {!isAgreementLoading ? (
              <DownloadIcon
                classes={['pointer']}
                onClick={handleDownloadAgreementBtnClick}
                dataTestId='orders-table-client-agreement-download-icon'
              />
            ) : (
              <CircularProgress size={20} />
            )}
          </>
        )
      case 'order_amount':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>{displayDollar(order_amount?.toString(), currency_code, 2, true)}</span>
          </ToolTip>
        )
      case 'total_amount_to_be_transferred':
        return (
          <ToolTip variant='table-cell' content={tooltipText}>
            <span>
              {displayDollar(total_amount_to_be_transferred?.toString(), currency_code, 2, true)}
            </span>
          </ToolTip>
        )
      case 'payment_copy':
        return (
          <>
            {payment_copy && (
              <EyeIcon
                classes={['align-center', 'pointer']}
                height='24px'
                width='24px'
                onClick={handlePaymentCopyClick}
                dataTestId='orders-table-payment-copy-view-icon'
              />
            )}
            {!payment_copy && transaction_reference_number && <>{transaction_reference_number}</>}
            {!payment_copy && !transaction_reference_number && <>N.A.</>}
          </>
        )
      case 'order_completed':
        return (
          <div className={'table-cell-with-icon'}>
            {order_type === 'BUY' && (
              <>
                {investment_completed ? 'Yes' : 'No'}
                {!investment_completed && (
                  <ToolTipBlack variant='pending-icon' classes={['ml-8']}>
                    {stuckAtInvestmentMessage[Number(completed_steps) + 1]?.stepDescription}
                  </ToolTipBlack>
                )}
              </>
            )}
            {order_type === 'SELL' && (
              <>
                {withdrawal_completed ? 'Yes' : 'No'}
                {!withdrawal_completed && (
                  <ToolTipBlack variant='pending-icon' classes={['ml-8']}>
                    {stuckAtWithdrawalMessage[Number(completed_steps) + 1]?.stepDescription}
                  </ToolTipBlack>
                )}
              </>
            )}
          </div>
        )
      case 'order_details':
        return (
          <EyeIcon
            classes={['align-center', 'pointer']}
            height='24px'
            width='24px'
            onClick={() => handleOrderClick()}
            dataTestId='orders-table-order-details-view-icon'
          />
        )
      default:
        return <></>
    }
  }

  return (
    <tr
      key={order_id}
      className={getTableClassNames({
        investor_account_status,
        investment_completed,
        completed_steps,
        payment_copy,
        transaction_reference_number,
        order_status,
        order_type,
        withdrawal_completed,
      })}
    >
      {columnOrder.map((columnId) => {
        const content = getCellContent(columnId)
        const isWelcomeLetter = columnId === 'welcome_letter'
        const isPaymentCopy = columnId === 'payment_copy'
        const isOrderCompleted = columnId === 'order_completed'

        if (isWelcomeLetter) {
          return (
            <td key={columnId} data-testid='orders-table-welcome-letter-cell'>
              {content}
            </td>
          )
        }
        if (isPaymentCopy) {
          return (
            <td key={columnId} data-testid='orders-table-payment-copy-cell'>
              {content}
            </td>
          )
        }
        if (isOrderCompleted) {
          return (
            <td key={columnId} data-testid='orders-table-order-completed-cell'>
              {content}
            </td>
          )
        }
        return <td key={columnId}>{content}</td>
      })}
    </tr>
  )
}
export { OrderCard }
